import { Card, CardContent, CardDetail, CardFront, CardHeader, CardStart } from '@plato/ui/containers/card';
import {
    SectionCard,
    SectionCardGroup,
    SectionCardGroupTitle,
    SectionCardStickyHeader,
    SectionCardTitle,
} from '@plato/ui/containers/section-card';
import { Button } from '@plato/ui/primitives/button';
import { Skeleton } from '@plato/ui/primitives/skeleton';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { InsightListEmpty } from '../../insights-common/components/insight-list-empty.tsx';
import { useInsightsWithAnalytics } from '../../insights-common/hooks/use-insights-with-analytics.ts';
import { isRiskInsightType } from '../../insights-common/lib/insight-bucket.ts';
import { CustomerDetailInsightCard } from './customer-detail-insight-card.tsx';

const maxVisiblePerGroup = 3;

/** Title widths per placeholder card, so the cards don't read as one repeated block. */
const loadingTitleWidths = ['w-3/5', 'w-4/5', 'w-1/2'];

export function CustomerDetailAnalysis({ customerId }: { customerId: string }): ReactElement {
    const { t } = useTranslation('customer-detail');

    const { insights, isLoading, sendAnalytics, setInsightVisibility } = useInsightsWithAnalytics({
        customerId,
    });

    const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(new Set());

    const groups = [
        {
            key: 'risks',
            title: t('risks', { ns: 'insights' }),
            insights: insights.filter((i) => isRiskInsightType(i.insight.insight_type)),
        },
        {
            key: 'opportunities',
            title: t('opportunities', { ns: 'insights' }),
            insights: insights.filter((i) => !isRiskInsightType(i.insight.insight_type)),
        },
    ].filter((group) => group.insights.length > 0);

    return (
        <SectionCard>
            <SectionCardStickyHeader>
                <SectionCardTitle>{t('analysis__title')}</SectionCardTitle>
            </SectionCardStickyHeader>
            {isLoading ? (
                <div className="flex flex-col gap-container-cozy">
                    {loadingTitleWidths.map((titleWidth) => (
                        // Mirrors InsightCardFront: name and value in the dark shade, icon and
                        // category in the light one, each on a line of the loaded card's height.
                        <Card key={titleWidth} aria-hidden>
                            <CardFront>
                                <CardStart>
                                    <Skeleton className="size-4 bg-content-weakest/50" />
                                </CardStart>
                                <CardContent className="min-w-0">
                                    <CardHeader className="h-5 flex-row flex-nowrap items-center gap-container">
                                        <Skeleton className={titleWidth} />
                                        <Skeleton className="ml-auto w-12 shrink-0" />
                                    </CardHeader>
                                    <CardDetail className="flex h-5 items-center">
                                        <Skeleton className="h-2.5 w-24 bg-content-weakest/50" />
                                    </CardDetail>
                                </CardContent>
                            </CardFront>
                        </Card>
                    ))}
                </div>
            ) : insights.length === 0 ? (
                <InsightListEmpty />
            ) : (
                <div className="flex flex-col gap-container">
                    {groups.map((group) => (
                        <SectionCardGroup
                            key={group.key}
                            aria-label={group.title}
                            className="gap-container-cozy"
                        >
                            <SectionCardGroupTitle>{group.title}</SectionCardGroupTitle>
                            {(expandedGroups.has(group.key)
                                ? group.insights
                                : group.insights.slice(0, maxVisiblePerGroup)
                            ).map((insightData) => (
                                <CustomerDetailInsightCard
                                    key={insightData.key}
                                    insightData={insightData}
                                    onAnalyticsEvent={sendAnalytics}
                                    onVisibleChanged={(isVisible) => {
                                        setInsightVisibility(insightData.insight.id, isVisible);
                                    }}
                                />
                            ))}
                            {group.insights.length > maxVisiblePerGroup && (
                                <Button
                                    variant="secondary"
                                    className="self-start"
                                    onClick={() => {
                                        setExpandedGroups((prev) => {
                                            const next = new Set(prev);
                                            if (!next.delete(group.key)) {
                                                next.add(group.key);
                                            }
                                            return next;
                                        });
                                    }}
                                >
                                    {expandedGroups.has(group.key)
                                        ? t('show_less', { ns: 'common' })
                                        : t('analysis__show_more', {
                                              count: group.insights.length - maxVisiblePerGroup,
                                          })}
                                </Button>
                            )}
                        </SectionCardGroup>
                    ))}
                </div>
            )}
        </SectionCard>
    );
}
